import React, { useState } from 'react';
import { 
  TextField, 
  Button, 
  Box, 
  Typography, 
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  IconButton,
  Grid,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CameraAlt, Upload, Percent } from '@mui/icons-material';
import heLocale from 'date-fns/locale/he';
import { useAuth } from '../auth/useAuth';
import PassportScanner from './PassportScanner';
import { initiatePayment } from '../api';

const SmartManualInput = ({ 
  open, 
  onClose, 
  busId, 
  busDetails, 
  systemSettings,
  onValidationComplete 
}) => {
  const { api } = useAuth();
  
  // מצבי הקומפוננט
  const [currentStep, setCurrentStep] = useState('input'); // input, details, manual_input, payment, credit_payment
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // נתונים
  const [identifier, setIdentifier] = useState(''); // תז או דרכון
  const [touristData, setTouristData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('מזומן');
  const [validationStatus, setValidationStatus] = useState(null);
  
  // מצבי הזנה ידנית וסריקה
  const [manualFormData, setManualFormData] = useState({
    idNumber: '',
    lastNameEn: '',
    firstNameEn: '',
    birthDate: null,
    passportNumber: '',
    citizenship: 'ישראלי'
  });
  const [isCameraScannerOpen, setIsCameraScannerOpen] = useState(false);
  const [isFileScannerOpen, setIsFileScannerOpen] = useState(false);
  
  // מצבי תשלום באשראי
  const [paymentUrl, setPaymentUrl] = useState('');
  const [paymentTransactionId, setPaymentTransactionId] = useState(null);
  const [paymentConfirmationKey, setPaymentConfirmationKey] = useState(null);
  const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
  const [paymentIframeRef, setPaymentIframeRef] = useState(null);
  
  // מצבי הנחה
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('');

  // 🆕 גלגול טיסה — סטייטים חדשים
  const [originMismatch, setOriginMismatch] = useState(null); // { correctOrigin, tourist }
  const [flightRolloverNote, setFlightRolloverNote] = useState('');

  // איפוס הטופס
  const resetForm = () => {
    setCurrentStep('input');
    setIdentifier('');
    setTouristData(null);
    setPaymentMethod('מזומן');
    setValidationStatus(null);
    setError('');
    setPaymentUrl('');
    setPaymentTransactionId(null);
    setPaymentConfirmationKey(null);
    setIsPaymentCompleted(false);
    setPaymentIframeRef(null);
    setManualFormData({
      idNumber: '',
      lastNameEn: '',
      firstNameEn: '',
      birthDate: null,
      passportNumber: '',
      citizenship: 'ישראלי'
    });
    setShowDiscountForm(false);
    setDiscountAmount('');
    setDiscountReason('');
    setSelectedDestination('');
    setOriginMismatch(null);
    setFlightRolloverNote('');
  };

  // סגירת הדיאלוג
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // שלב 1: בדיקת הזמנה קיימת (כולל טיפול ב-origin_mismatch)
  const handleIdentifierSubmit = async () => {
    if (!identifier.trim()) {
      setError('נא להזין תעודת זהות או מספר דרכון');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // אם אין busId (מגיעים מרשימת תיירים), עבור ישר להזנה ידנית
      if (!busId) {
        console.log('🔍 [DEBUG] No busId provided - switching to manual input mode');
        setCurrentStep('manual_input');
        setIsLoading(false);
        return;
      }

      // המשך עם הלוגיקה הרגילה רק אם יש busId
      console.log('🔍 [DEBUG] Frontend - Sending request to check-tourist-status:');
      console.log('  - identifier:', identifier.trim());
      console.log('  - busId:', busId);
      console.log('  - busDetails:', busDetails);
      
      const response = await api.post('/external-api/check-tourist-status', {
        identifier: identifier.trim(),
        busId,
        busDetails
      });

      console.log('🔍 [DEBUG] Frontend - Response received:');
      console.log('  - status:', response.data.status);
      console.log('  - message:', response.data.message);
      console.log('  - data:', response.data.data);

      const { status, data, message } = response.data;
      setValidationStatus(status);

      switch (status) {
        case 'can_validate':
          // יש נסיעות ויעד/מוצא נכון - תיקוף ישיר
          await handleDirectValidation(data);
          break;
          
        case 'origin_mismatch': // 🆕 במקרה של חוסר התאמה במוצא בהלוך
          setOriginMismatch({ correctOrigin: data.correctOrigin, tourist: data.tourist });
          break;

        case 'wrong_destination':
          // יש נסיעות אבל יעד לא נכון (בחזור)
          setError(`זה לא היעד שלך. היעד שלך הוא: ${data.correctDestination}`);
          break;
          
        case 'has_flight_no_travel':
          // יש טיסה אין נסיעות - מעבר לפרטים
          setTouristData(data);
          setCurrentStep('details');
          break;
          
        case 'not_found':
          // לא נמצא בפרויקט - קריאה ל-API חיצוני
          await fetchFromExternalApi();
          break;
          
        case 'wrong_direction':
          // כיוון לא נכון
          setError(message || 'כיוון הנסיעה לא תואם');
          break;
          
        default:
          setError(message || 'שגיאה לא צפויה');
      }
    } catch (error) {
      console.error('שגיאה בבדיקת סטטוס:', error);
      setError(error.response?.data?.message || 'שגיאה בבדיקת הנוסע');
    } finally {
      setIsLoading(false);
    }
  };

  // תיקוף ישיר
  const handleDirectValidation = async (touristData) => {
    try {
      // כאן נקרא לפונקציה הקיימת של תיקוף
      await onValidationComplete({
        type: 'direct_validation',
        data: touristData
      });
      handleClose();
    } catch (error) {
      setError('שגיאה בתיקוף הנוסע');
    }
  };

  // המרת תאריך מפורמט API חיצוני לפורמט ISO
  const convertDateToISO = (dateStr) => {
    if (!dateStr) return null;
    
    try {
      // פורמט: 11-JUL-83
      const [day, monthStr, year] = dateStr.split('-');
      
      // מיפוי חודשים
      const monthMap = {
        'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
        'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
        'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
      };
      
      const month = monthMap[monthStr];
      if (!month) return null;
      
      // המרת שנה - אם פחות מ-50 זה 20xx, אחרת 19xx
      let fullYear = parseInt(year);
      if (fullYear < 50) {
        fullYear = 2000 + fullYear;
      } else {
        fullYear = 1900 + fullYear;
      }
      
      // יצירת תאריך ISO
      const isoDate = `${fullYear}-${month}-${day.padStart(2, '0')}T00:00:00.000Z`;
      return isoDate;
    } catch (error) {
      console.error('שגיאה בהמרת תאריך:', error);
      return null;
    }
  };

  // קריאה ל-API חיצוני
  const fetchFromExternalApi = async () => {
    try {
      const response = await api.get(`/external-api/tourist-from-external/${identifier}`);
      
      if (response.data && response.data.success && response.data.data.length > 0) {
        const externalData = response.data.data[0];
        
        // המרת הנתונים לפורמט שלנו
        const convertedData = {
          idNumber: externalData.TZ,
          firstNameEn: externalData.FIRST_NAME,
          lastNameEn: externalData.LAST_NAME,
          passportNumber: externalData.PASSPORT,
          birthDate: convertDateToISO(externalData.TAARICH_LEDA),
          citizenship: externalData.EZRACHUT === 'ISRAEL' ? 'ישראלי' : 'אחר',
          source: 'external'
        };
        
        setTouristData(convertedData);
        setCurrentStep('details');
      } else {
        // לא נמצא במאגר - מעבר להזנה ידנית
        setCurrentStep('manual_input');
      }
    } catch (error) {
      console.error('שגיאה בקריאה מ-API חיצוני:', error);
      setError('שגיאה בחיפוש במאגר החיצוני');
    }
  };

  // פורמט תאריך להצגה
  const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return '';
    try {
      const date = new Date(isoDate);
      return date.toLocaleDateString('he-IL');
    } catch (error) {
      return isoDate; // אם יש שגיאה, החזר את המקורי
    }
  };

  // טיפול בשינויים בטופס הידני
  const handleManualInputChange = (e) => {
    const { name, value } = e.target;
    setManualFormData(prev => ({ ...prev, [name]: value }));
  };

  // טיפול בשינוי תאריך בטופס הידני
  const handleManualDateChange = (date) => {
    setManualFormData(prev => ({ ...prev, birthDate: date }));
  };

  // טיפול בתוצאות סריקת דרכון
  const handleScanComplete = (scannedData) => {
    if (scannedData) {
      setManualFormData(prev => ({
        ...prev,
        ...scannedData
      }));
    }
    setIsCameraScannerOpen(false);
    setIsFileScannerOpen(false);
  };

  // מעבר מהזנה ידנית לתשלום
  const handleManualFormSubmit = () => {
    // בדיקת שדות חובה
    if (!manualFormData.firstNameEn || !manualFormData.lastNameEn) {
      setError('נא למלא שם פרטי ושם משפחה');
      return;
    }

    // אם אין busId (רשימת תיירים), נדרש גם יעד
    if (!busId && !selectedDestination) {
      setError('נא לבחור יעד נסיעה');
      return;
    }

    // המרת הנתונים לפורמט של touristData
    const convertedData = {
      idNumber: manualFormData.idNumber,
      firstNameEn: manualFormData.firstNameEn,
      lastNameEn: manualFormData.lastNameEn,
      passportNumber: manualFormData.passportNumber,
      birthDate: manualFormData.birthDate ? manualFormData.birthDate.toISOString() : null,
      citizenship: manualFormData.citizenship,
      selectedDestination: selectedDestination, // הוסף את היעד שנבחר
      source: 'manual'
    };

    setTouristData(convertedData);
    setCurrentStep('details');
  };

  // טיפול בתשלום ותיקוף
  const handlePaymentAndValidation = async () => {
    setIsLoading(true);
    setError('');

    try {
      const originalPrice = calculatePrice();
      const finalPrice = calculateFinalPrice();
      const discountApplied = parseFloat(discountAmount) > 0;
      
      const validationData = {
        ...touristData,
        paymentMethod,
        calculatedPrice: finalPrice,
        originalAmount: discountApplied ? originalPrice : null,
        discountAmount: discountApplied ? parseFloat(discountAmount) : null,
        discountReason: discountApplied ? discountReason : null,
      };

      // הוסף נתונים ספציפיים להקשר
      if (busId) {
        validationData.busId = busId;
        validationData.originOrDestination = getOriginOrDestination();
      } else {
        // במקרה של tourist-list, השתמש ביעד שנבחר
        validationData.originOrDestination = touristData.selectedDestination || selectedDestination;
      }

      if (paymentMethod === 'אשראי') {
        // טיפול בתשלום אשראי
        await handleCreditPayment(validationData);
      } else {
        // תשלום מזומן או פאייכלה
        await onValidationComplete({
          type: 'manual_validation',
          data: validationData
        });
        handleClose();
      }
    } catch (error) {
      console.error('שגיאה בעיבוד:', error);
      setError('שגיאה בעיבוד התשלום');
    } finally {
      setIsLoading(false);
    }
  };

  // חישוב מחיר (מותאם לשני ההקשרים)
  const calculatePrice = () => {
    if (!systemSettings) return 0;
    
    let locationForPrice, isOutbound;
    
    if (busDetails) {
      // הקשר של תיקוף אוטובוס
      isOutbound = busDetails?.direction === 'H' || busDetails?.direction === 'הלוך';
      locationForPrice = isOutbound ? busDetails.origin : busDetails.destination;
    } else {
      // הקשר של רשימת תיירים - לפי מצב הנסיעה הכללי
      const travelMode = systemSettings?.find(s => s.key === 'travel_mode')?.value;
      isOutbound = travelMode === 'הלוך';
      locationForPrice = touristData?.selectedDestination || selectedDestination || '';
    }
    
    if (!locationForPrice) return 0;
    
    // חיפוש מחיר בהגדרות המערכת
    const priceKey = isOutbound ? 'departure_prices' : 'return_prices';
    const priceSettings = systemSettings?.find(s => s.key === priceKey)?.value || {};
    
    const price = priceSettings[locationForPrice] || 0;
    const finalPrice = parseFloat(price) || 0;
    
    console.log('🔍 Price calculation debug:');
    console.log('  - locationForPrice:', locationForPrice);
    console.log('  - priceKey:', priceKey);
    console.log('  - priceSettings:', priceSettings);
    console.log('  - raw price:', price);
    console.log('  - final price:', finalPrice);
    
    return finalPrice;
  };

  // חישוב מחיר סופי עם הנחה
  const calculateFinalPrice = () => {
    const originalPrice = calculatePrice();
    const discount = parseFloat(discountAmount) || 0;
    const finalPrice = Math.max(0, originalPrice - discount);
    // עיגול ל-2 ספרות אחרי הנקודה כדי למנוע בעיות דיוק
    return Math.round(finalPrice * 100) / 100;
  };

  // קבלת מוצא או יעד
  const getOriginOrDestination = () => {
    if (busDetails) {
      const isOutbound = busDetails?.direction === 'H' || busDetails?.direction === 'הלוך';
      return isOutbound ? busDetails.origin : busDetails.destination;
    } else {
      // במקרה של רשימת תיירים, השתמש ביעד שנבחר
      return touristData?.selectedDestination || selectedDestination || '';
    }
  };

  // טיפול בתשלום אשראי
  const handleCreditPayment = async (validationData) => {
    setIsLoading(true);
    try {
      const originalAmount = calculatePrice();
      const calculatedAmount = calculateFinalPrice();
      console.log('🔍 Original price:', originalAmount);
      console.log('🔍 Final price after discount:', calculatedAmount);
      
      const paymentData = {
        amount: calculatedAmount,
        currency: "2",
        description: `תשלום עבור נסיעה ל${getOriginOrDestination()}`,
        customerName: `${validationData.firstNameEn} ${validationData.lastNameEn}`,
        customerId: validationData.idNumber || validationData.passportNumber,
        paramX: paymentTransactionId,
      };
      
      console.log('💳 Payment data being sent:', paymentData);

      const paymentResponse = await initiatePayment(paymentData);
      
      if (paymentResponse.success) {
        // שמירת פרטי התשלום ומעבר לשלב התשלום באייפרם
        setPaymentUrl(paymentResponse.paymentUrl);
        setPaymentTransactionId(paymentResponse.transactionId);
        setPaymentConfirmationKey(paymentResponse.confirmationKey);
        setCurrentStep('credit_payment');
        setError('');
      } else {
        throw new Error('Payment initiation failed');
      }
    } catch (error) {
      console.error('שגיאה בתשלום באשראי:', error);
      setError('שגיאה בעיבוד התשלום. אנא נסה שוב.');
    } finally {
      setIsLoading(false);
    }
  };

  // מעקב אחר שינויי URL באייפרם
  const handleIframeLoad = () => {
    if (!paymentIframeRef) return;
    
    console.log('🔍 [DEBUG] Checking iframe status...');
    
    try {
      // ניסיון לגשת ל-URL של האייפרם
      const iframeUrl = paymentIframeRef.contentWindow?.location?.href;
      const iframeTitle = paymentIframeRef.contentDocument?.title || '';
      const iframeContent = paymentIframeRef.contentDocument?.body?.innerText || '';
      
      console.log('🌐 [DEBUG] Iframe access status:');
      console.log('   URL accessible:', !!iframeUrl);
      console.log('   Title accessible:', !!iframeTitle);  
      console.log('   Content accessible:', !!iframeContent);
      console.log('   Current payment completed status:', isPaymentCompleted);
      
      if (iframeUrl) {
        console.log('✅ [DEBUG] Full URL access:', iframeUrl);
        console.log('📄 [DEBUG] Page title:', iframeTitle);
        console.log('📝 [DEBUG] Content sample:', iframeContent.substring(0, 150));
        
        // בדיקה מפורטת של כל תנאי הצלחה
        const successConditions = {
          statusCode000: iframeUrl.includes('PelecardStatusCode=000'),
          approvalNo: iframeUrl.includes('ApprovalNo='),
          successInUrl: iframeUrl.includes('success'),
          approvedInUrl: iframeUrl.includes('approved'),
          successInTitle: iframeTitle.includes('success'),
          successHebrewTitle: iframeTitle.includes('הצלחה'),
          approvedHebrewTitle: iframeTitle.includes('אושר'),
          paymentApprovedContent: iframeContent.includes('תשלום אושר'),
          transactionSuccessContent: iframeContent.includes('העסקה בוצעה בהצלחה'),
          returnButtonContent: iframeContent.includes('חזור למערכת'),
          paymentCompletedTitle: iframeTitle.includes('תשלום הושלם')
        };
        
        console.log('🎯 [DEBUG] Success condition check:', successConditions);
        
        const hasSuccess = Object.values(successConditions).some(condition => condition);
        
        if (hasSuccess) {
          console.log('🎉 [DEBUG] SUCCESS DETECTED! Activating button...');
          setIsPaymentCompleted(true);
        }
        
        // בדיקה אם זה דף כישלון
        const failureConditions = {
          errorInUrl: iframeUrl.includes('/payment/error'),
          errorKeyword: iframeUrl.includes('error'),
          failedKeyword: iframeUrl.includes('failed'),
          nonZeroStatusCode: (iframeUrl.includes('PelecardStatusCode') && !iframeUrl.includes('PelecardStatusCode=000'))
        };
        
        console.log('❌ [DEBUG] Failure condition check:', failureConditions);
        
        const hasFailure = Object.values(failureConditions).some(condition => condition);
        
        if (hasFailure) {
          console.log('💥 [DEBUG] FAILURE DETECTED!');
          setError('התשלום נכשל. אנא נסה שוב או בחר אמצעי תשלום אחר.');
        }
      } else {
        console.log('🚫 [DEBUG] Cannot access URL due to CORS - relying on messages only');
        
        // בדיקה של תוכן וכותרת גם ללא URL
        if (iframeTitle) {
          console.log('📄 [DEBUG] Checking title only:', iframeTitle);
          if (iframeTitle.includes('הצלחה') || iframeTitle.includes('success') || iframeTitle.includes('אושר')) {
            console.log('🎉 [DEBUG] SUCCESS from title!');
            setIsPaymentCompleted(true);
          }
        }
        
        if (iframeContent) {
          console.log('📝 [DEBUG] Checking content only...');
          if (iframeContent.includes('תשלום אושר') || 
              iframeContent.includes('חזור למערכת') ||
              iframeContent.includes('העסקה בוצעה בהצלחה')) {
            console.log('🎉 [DEBUG] SUCCESS from content!');
            setIsPaymentCompleted(true);
          }
        }
      }
    } catch (error) {
      console.log('🚫 [DEBUG] CORS Error details:', error.message);
      console.log('🔄 [DEBUG] Will rely on messages and manual detection...');
    }
  };

  // האזנה להודעות מהאייפרם
  React.useEffect(() => {
    const handleMessage = (event) => {
      console.log('📨 [DEBUG] Message received from iframe:');
      console.log('   Origin:', event.origin);
      console.log('   Data:', event.data);
      console.log('   Data type:', typeof event.data);
      
      // בדיקת מקור ההודעה (אבטחה)
      const validOrigins = [
        window.location.origin,
        'https://gateway21.pelecard.biz',
        'https://uman-api.shimon-k.com'
      ];
      
      const isValidOrigin = validOrigins.some(origin => event.origin.includes(origin)) ||
                           event.origin.includes('pelecard') ||
                           event.origin.includes('payment');
      
      if (!isValidOrigin) {
        console.log('🚫 [DEBUG] Message from invalid origin, ignoring');
        return;
      }
      
      console.log('✅ [DEBUG] Message from valid origin, processing...');
      
      // בדיקה אם זה הודעת הצלחה
      const successIndicators = {
        typeSuccess: event.data?.type === 'payment_success',
        statusSuccess: event.data?.status === 'success',
        approved: event.data?.approved === true,
        stringIncludes: (typeof event.data === 'string' && event.data.includes('success'))
      };
      
      console.log('🎯 [DEBUG] Message success indicators:', successIndicators);
      
      const hasSuccess = Object.values(successIndicators).some(indicator => indicator);
      
      if (hasSuccess) {
        console.log('🎉 [DEBUG] Payment success detected via message!');
        setIsPaymentCompleted(true);
      }
    };

    if (currentStep === 'credit_payment') {
      window.addEventListener('message', handleMessage);
      
      // בדיקה תקופתית של סטטוס התשלום
      let checkCount = 0;
      const checkPaymentStatus = setInterval(() => {
        checkCount++;
        console.log(`⏰ [DEBUG] Periodic check #${checkCount} (every 2 seconds)`);
        
        // בדיקה ידנית של כותרת האייפרם
        if (paymentIframeRef && paymentIframeRef.contentDocument) {
          try {
            const iframeTitle = paymentIframeRef.contentDocument.title || '';
            console.log('📄 [DEBUG] Iframe title check:', iframeTitle);
            
            if (iframeTitle.includes('הצלחה') || 
                iframeTitle.includes('success') || 
                iframeTitle.includes('אושר') ||
                iframeTitle.includes('completed')) {
              console.log('🎉 [DEBUG] SUCCESS detected from iframe title!');
              setIsPaymentCompleted(true);
              clearInterval(checkPaymentStatus);
              return;
            }
          } catch (error) {
            console.log('🚫 [DEBUG] Cannot access iframe title:', error.message);
          }
        }
        
        handleIframeLoad();
        
        // הפסק בדיקה אחרי 5 דקות (150 בדיקות)
        if (checkCount >= 150) {
          console.log('⏰ [DEBUG] Stopping periodic checks after 5 minutes');
          clearInterval(checkPaymentStatus);
        }
      }, 2000);

      return () => {
        window.removeEventListener('message', handleMessage);
        clearInterval(checkPaymentStatus);
      };
    }
  }, [currentStep, paymentIframeRef]);

  // טיפול בהשלמת תשלום
  const handlePaymentComplete = async () => {
    setIsLoading(true);
    try {
      // הוסף פרטי תשלום לנתונים
      const originalPrice = calculatePrice();
      const finalPrice = calculateFinalPrice();
      const discountApplied = parseFloat(discountAmount) > 0;
      
      const validationData = {
        ...touristData,
        paymentTransactionId,
        paymentMethod: 'אשראי',
        calculatedPrice: finalPrice,
        originalAmount: discountApplied ? originalPrice : null,
        discountAmount: discountApplied ? parseFloat(discountAmount) : null,
        discountReason: discountApplied ? discountReason : null,
        originOrDestination: getOriginOrDestination(),
        confirmationKey: paymentConfirmationKey // הוספת ConfirmationKey
      };
      
      await onValidationComplete({
        type: 'manual_validation',
        data: validationData
      });
      handleClose();
    } catch (error) {
      console.error('שגיאה בהשלמת התיקוף:', error);
      setError('שגיאה בהשלמת התיקוף. אנא נסה שוב.');
    } finally {
      setIsLoading(false);
    }
  };

  // קבלת טקסט כפתור התשלום
  const getPaymentButtonText = () => {
    switch (paymentMethod) {
      case 'מזומן':
        return 'התקבל תשלום - בצע תיקוף';
      case 'פאייכלה':
        return 'פאייכלה - בצע תיקוף';
      case 'אשראי':
        return 'בצע תשלום באשראי';
      default:
        return 'בצע תיקוף';
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          הוספת נוסע
        </DialogTitle>
        
        <DialogContent>
          {/* שלב 1: הזנת מזהה */}
          {currentStep === 'input' && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                הזן תעודת זהות או מספר דרכון:
              </Typography>
              
              <TextField
                fullWidth
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="תעודת זהות או מספר דרכון"
                disabled={isLoading}
                sx={{ mb: 2 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleIdentifierSubmit();
                  }
                }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={handleIdentifierSubmit}
                disabled={isLoading || !identifier.trim()}
                sx={{ mt: 2 }}
              >
                {isLoading ? <CircularProgress size={24} /> : 'המשך'}
              </Button>
            </Box>
          )}

          {/* שלב 2: הזנה ידנית */}
          {currentStep === 'manual_input' && (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
              <Box sx={{ pt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  הזן פרטי נוסע ידנית
                </Typography>

                {/* כפתורי סריקה */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<CameraAlt />}
                      onClick={() => setIsCameraScannerOpen(true)}
                      disabled={isLoading}
                    >
                      צלם דרכון
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Upload />}
                      onClick={() => setIsFileScannerOpen(true)}
                      disabled={isLoading}
                    >
                      העלה תמונה
                    </Button>
                  </Grid>
                </Grid>

                {/* שדות הטופס */}
                <TextField
                  fullWidth
                  name="idNumber"
                  label="מספר זהות"
                  value={manualFormData.idNumber}
                  onChange={handleManualInputChange}
                  sx={{ mb: 2 }}
                  disabled={isLoading}
                />

                <TextField
                  fullWidth
                  name="firstNameEn"
                  label="שם פרטי (אנגלית)"
                  value={manualFormData.firstNameEn}
                  onChange={handleManualInputChange}
                  sx={{ mb: 2 }}
                  disabled={isLoading}
                  required
                />

                <TextField
                  fullWidth
                  name="lastNameEn"
                  label="שם משפחה (אנגלית)"
                  value={manualFormData.lastNameEn}
                  onChange={handleManualInputChange}
                  sx={{ mb: 2 }}
                  disabled={isLoading}
                  required
                />

                <TextField
                  fullWidth
                  name="passportNumber"
                  label="מספר דרכון"
                  value={manualFormData.passportNumber}
                  onChange={handleManualInputChange}
                  sx={{ mb: 2 }}
                  disabled={isLoading}
                />

                <DatePicker
                  label="תאריך לידה"
                  value={manualFormData.birthDate}
                  onChange={handleManualDateChange}
                  disabled={isLoading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: { mb: 2 }
                    }
                  }}
                />

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>אזרחות</InputLabel>
                  <Select
                    name="citizenship"
                    value={manualFormData.citizenship}
                    onChange={handleManualInputChange}
                    disabled={isLoading}
                  >
                    <MenuItem value="ישראלי">ישראלי</MenuItem>
                    <MenuItem value="אחר">אחר</MenuItem>
                  </Select>
                </FormControl>

                {!busId && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>
                      {(() => {
                        const travelMode = systemSettings?.find(s => s.key === 'travel_mode')?.value;
                        return travelMode === 'הלוך' ? 'מוצא' : 'יעד';
                      })()} *
                    </InputLabel>
                    <Select
                      value={selectedDestination}
                      onChange={(e) => setSelectedDestination(e.target.value)}
                      disabled={isLoading}
                      required
                    >
                      <MenuItem value="">
                        <em>בחר {(() => {
                          const travelMode = systemSettings?.find(s => s.key === 'travel_mode')?.value;
                          return travelMode === 'הלוך' ? 'מוצא' : 'יעד';
                        })()}</em>
                      </MenuItem>
                      {(() => {
                        const travelMode = systemSettings?.find(s => s.key === 'travel_mode')?.value;
                        const isOutbound = travelMode === 'הלוך';
                        const priceKey = isOutbound ? 'departure_prices' : 'return_prices';
                        const destinations = systemSettings?.find(s => s.key === priceKey)?.value || {};
                        
                        return Object.keys(destinations).map((dest) => (
                          <MenuItem key={dest} value={dest}>
                            {dest}
                          </MenuItem>
                        ));
                      })()}
                    </Select>
                  </FormControl>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {/* כפתורי פעולה */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentStep('input')}
                    disabled={isLoading}
                    sx={{ flex: 1 }}
                  >
                    חזור
                  </Button>
                  
                  <Button
                    variant="contained"
                    onClick={handleManualFormSubmit}
                    disabled={isLoading}
                    sx={{ flex: 2 }}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'המשך'}
                  </Button>
                </Box>
              </Box>
            </LocalizationProvider>
          )}

          {/* שלב 3: הצגת פרטים ובחירת תשלום */}
          {currentStep === 'details' && touristData && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                פרטי הנוסע:
              </Typography>

              {/* הצגת הפרטים */}
              <Box sx={{ mb: 3 }}>
                <Typography><strong>שם פרטי:</strong> {touristData.firstNameEn}</Typography>
                <Typography><strong>שם משפחה:</strong> {touristData.lastNameEn}</Typography>
                <Typography><strong>מספר זהות:</strong> {touristData.idNumber}</Typography>
                <Typography><strong>מספר דרכון:</strong> {touristData.passportNumber}</Typography>
                <Typography><strong>תאריך לידה:</strong> {formatDateForDisplay(touristData.birthDate)}</Typography>
                
                {/* הצגת מחיר */}
                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                  {parseFloat(discountAmount) > 0 ? (
                    <Box>
                      <Typography variant="h6" sx={{ color: 'primary.contrastText', mb: 1 }}>
                        <strong>מחיר מקורי: ${calculatePrice()}</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'primary.contrastText' }}>
                        הנחה: ${Math.round((parseFloat(discountAmount) || 0) * 100) / 100} ({discountReason})
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                        <strong>מחיר סופי: ${calculateFinalPrice()}</strong>
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="h6" sx={{ color: 'primary.contrastText' }}>
                      <strong>מחיר: ${calculatePrice()}</strong>
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: 'primary.contrastText' }}>
                    {busDetails?.direction === 'H' || busDetails?.direction === 'הלוך' ? 'מוצא' : 'יעד'}: {getOriginOrDestination()}
                  </Typography>
                </Box>

                {/* כפתור הנחה וטופס הנחה */}
                <Box sx={{ mt: 2 }}>
                  {!showDiscountForm ? (
                    <Button
                      variant="outlined"
                      color={parseFloat(discountAmount) > 0 ? "success" : "secondary"}
                      onClick={() => setShowDiscountForm(true)}
                      startIcon={<Percent />}
                      sx={{ mb: 2 }}
                    >
                      {parseFloat(discountAmount) > 0 ? "ערוך הנחה" : "הוסף הנחה"}
                    </Button>
                  ) : (
                    <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1, mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                        הנחה
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="סכום הנחה ($)"
                            type="number"
                            value={discountAmount}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              const maxDiscount = calculatePrice();
                              if (value <= maxDiscount) {
                                setDiscountAmount(e.target.value);
                              }
                            }}
                            inputProps={{ min: 0, max: calculatePrice(), step: 0.01 }}
                            helperText={`מקסימום: $${calculatePrice()}`}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="סיבת הנחה"
                            value={discountReason}
                            onChange={(e) => setDiscountReason(e.target.value)}
                            placeholder="למה ניתנת ההנחה?"
                          />
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => setShowDiscountForm(false)}
                          disabled={parseFloat(discountAmount) > 0 && !discountReason.trim()}
                        >
                          אישור הנחה
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setShowDiscountForm(false);
                            setDiscountAmount('');
                            setDiscountReason('');
                          }}
                        >
                          ביטול
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* בחירת אמצעי תשלום */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>אמצעי תשלום</InputLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  disabled={isLoading}
                >
                  <MenuItem value="מזומן">מזומן</MenuItem>
                  <MenuItem value="פאייכלה">פאייכלה</MenuItem>
                  <MenuItem value="אשראי">אשראי</MenuItem>
                </Select>
              </FormControl>

              {/* הודעה מיוחדת לתשלום אשראי */}
              {paymentMethod === 'אשראי' && calculateFinalPrice() > 0 && !isLoading && (
                <Box 
                  sx={{ 
                    mt: 1, 
                    mb: 2,
                    p: 1, 
                    backgroundColor: 'success.light', 
                    borderRadius: 1,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="body2" color="success.contrastText">
                    תשלום באשראי - לחץ על הכפתור להמשך לתשלום
                  </Typography>
                  <Typography variant="caption" color="success.contrastText" sx={{ display: 'block', mt: 1 }}>
                    אם הכפתור לא הופך לפעיל אוטומטית, לחץ על "התשלום הושלם ידנית" למטה.
                  </Typography>
                </Box>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* כפתורי פעולה */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setCurrentStep('input')}
                  disabled={isLoading}
                  sx={{ flex: 1 }}
                >
                  חזור
                </Button>
                
                <Button
                  variant="contained"
                  onClick={handlePaymentAndValidation}
                  disabled={isLoading}
                  sx={{ flex: 2 }}
                >
                  {isLoading ? <CircularProgress size={24} /> : getPaymentButtonText()}
                </Button>
              </Box>
            </Box>
          )}

          {/* שלב 4: תשלום באשראי באייפרם */}
          {currentStep === 'credit_payment' && paymentUrl && (
            <Box sx={{ pt: 2, height: '70vh', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                השלם תשלום באשראי
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
                מחיר: ${calculatePrice()} • {getOriginOrDestination()}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* האייפרם לתשלום */}
              <Box sx={{ flex: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                <iframe
                  ref={(ref) => setPaymentIframeRef(ref)}
                  src={paymentUrl}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title="תשלום באשראי"
                  onLoad={handleIframeLoad}
                  sandbox="allow-same-origin allow-scripts allow-forms allow-top-navigation allow-popups"
                />
              </Box>

              {/* כפתורי פעולה */}
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setCurrentStep('details')}
                  disabled={isLoading}
                  sx={{ flex: 1 }}
                >
                  חזור
                </Button>
                
                <Button
                  variant="contained"
                  color={isPaymentCompleted ? "success" : "primary"}
                  onClick={handlePaymentComplete}
                  disabled={isLoading || !isPaymentCompleted}
                  sx={{ 
                    flex: 2,
                    opacity: isPaymentCompleted ? 1 : 0.6,
                    transition: 'all 0.3s ease'
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : isPaymentCompleted ? (
                    '✅ התשלום הושלם - המשך לתיקוף'
                  ) : (
                    '⏳ ממתין לאישור תשלום...'
                  )}
                </Button>
              </Box>

              {/* כפתור ידני לתשלום אשראי */}
              {paymentMethod === 'אשראי' && !isLoading && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={async () => {
                      console.log('🔧 [DEBUG] Manual payment completion triggered');
                      setIsPaymentCompleted(true);
                      
                      // עדכן את הטרנזקציה ידנית
                      if (paymentTransactionId) {
                        try {
                          await api.put(`/transactions/${paymentTransactionId}`, {
                            status: 'completed',
                            processedAt: new Date()
                          });
                          console.log('✅ Transaction updated manually to completed');
                        } catch (error) {
                          console.error('❌ Failed to update transaction:', error);
                        }
                      }
                    }}
                    size="small"
                  >
                    התשלום הושלם ידנית
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    אם הכפתור למעלה לא הופך לפעיל אוטומטית
                  </Typography>
                </Box>
              )}

              {/* אינדיקטור סטטוס */}
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                {isPaymentCompleted ? (
                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                    🎉 התשלום אושר בהצלחה! ניתן להמשיך
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    אנא השלם את התשלום באייפרם למעלה
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* 🆕 דיאלוג גלגול טיסה */}
      <Dialog open={!!originMismatch} onClose={() => setOriginMismatch(null)}>
        <DialogTitle>המוצא לא תואם</DialogTitle>
        <DialogContent>
          <DialogContentText>
            המוצא הוא {originMismatch?.correctOrigin || 'לא ידוע'}. כדי לתקף בכל זאת, כתוב גלגול טיסה:
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={flightRolloverNote}
            onChange={(e) => setFlightRolloverNote(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="גלגול טיסה"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOriginMismatch(null)}>ביטול</Button>
          <Button
            variant="contained"
            disabled={!flightRolloverNote.trim() || isLoading}
            onClick={async () => {
              try {
                setIsLoading(true);
                const barcodeToValidate = originMismatch?.tourist?.MISPAR_HAZMANA;
                if (!barcodeToValidate) throw new Error('אין מספר הזמנה תקין');

                await api.post('/passengers/validate', {
                  busId,
                  barcode: barcodeToValidate,
                  flightRolloverNote: flightRolloverNote.trim(),
                });

                await onValidationComplete({
                  type: 'direct_validation',
                  data: originMismatch.tourist
                });

                setFlightRolloverNote('');
                setOriginMismatch(null);
                handleClose(); // סגירת הדיאלוג הראשי
              } catch (err) {
                console.error(err);
                setError(err.response?.data?.message || 'שגיאה בתיקוף');
              } finally {
                setIsLoading(false);
              }
            }}
          >
            אישור
          </Button>
        </DialogActions>
      </Dialog>

      {/* קומפוננטי סריקת דרכון */}
      <PassportScanner
        open={isCameraScannerOpen}
        onClose={() => setIsCameraScannerOpen(false)}
        onScanComplete={handleScanComplete}
        mode="camera"
        maxWidth="sm"
        maxHeight="70vh"
      />
      
      <PassportScanner
        open={isFileScannerOpen}
        onClose={() => setIsFileScannerOpen(false)}
        onScanComplete={handleScanComplete}
        mode="file"
        maxWidth="sm"
        maxHeight="70vh"
      />
    </>
  );
};

export default SmartManualInput;
